import { Form, Input } from "decorator-form";
import React from "react";
import { Entity, Field } from "typegraph";
import convertToHtml from "common/fragments/ConvertToHtml";
import { ID } from "common/entities/IEntity";
import InputType from "common/form/InputType";
import { IPageableListQuery } from "common/models/IPageableListQuery";
import IEntity from "core/util/IEntity";
import CountResult from "panels/organizationParticipant/entities/CountResult";
import entityMeta from "../entityMeta";
import { Button, Classes, Dialog } from "@blueprintjs/core";
import Query from "core/api/QueryComponent";
import AppToaster from "core/util/AppToaster";
import Class from "core/util/Class";
import EntityForm from "core/components/EntityForm";
import Label from "core/components/label/Label";
import Multiselect from "multiselect-react-dropdown";
import ServiceRegistry from "core/util/ServiceRegistry";
import Loading from "common/fragments/Loading";

@Entity(entityMeta.GLOSSARY_TERM)
@Form()
class GlossaryTerm implements IEntity {
	@Field()
	public id: ID;

	@Field()
	@Input({
		type: InputType.TEXT,
		title: "Term",
		required: true
	})
	public term: string;

	@Field()
	@Input({
		type: InputType.TEXT,
		title: "Alternatives"
	})
	public alternatives: string;

	@Field()
	@Input({
		type: InputType.TEXT_HTML,
		title: "Definition",
		required: true
	})
	public definition: string;
}

function mapQueryParams(args: any) {
	const params = {};

	Object.entries(args).forEach(([key, value]) => {
		if (value) {
			params[key] = value;
		}
	});

	return { params };
}

class GlossaryTermQuery implements IPageableListQuery<GlossaryTerm> {
	@Field({
		entity: GlossaryTerm,
		aliasFor: entityMeta.GLOSSARY_TERM.many,
		params: mapQueryParams
	})
	public entities: null | GlossaryTerm[];

	@Field({
		entity: CountResult,
		aliasFor: "glossaryTerm_count",
		params: mapQueryParams
	})
	public count: CountResult;
}

interface IGlossaryTermListProps {
	articleId: ID;
	knowledgeBaseId: ID;
	canCreate?: boolean;
	canDelete?: boolean;
	canUpdate?: boolean;
	showPaginationOptions?: boolean;
}

export default function ArticleGlossaryTermList(props: IGlossaryTermListProps) {
	const params = {};
	const paramsArticle = {};
	if (props.knowledgeBaseId) {
		params["knowledgeBaseId"] = props.knowledgeBaseId;
		paramsArticle["knowledgeBaseId"] = props.knowledgeBaseId;
	} else {
		return null;
	}

	if (props.articleId) {
		paramsArticle["articleId"] = props.articleId;
	}
	return (
		<div>
			<GlossarySearch
				pagingQuery={GlossaryTermQuery}
				formClass={GlossaryTerm}
				params={params}
				paramsArticle={paramsArticle}
				canCreate={props.canCreate}
				onBeforeSave={term => {
					const actionParams = {};

					if (term.id) {
						actionParams["id"] = term.id;
					} else {
						actionParams["knowledgeBaseId"] = props.knowledgeBaseId;
					}
					if (props.articleId) {
						actionParams["articleId"] = props.articleId;
					}

					actionParams["term"] = term.term;

					if (term.alternatives) {
						actionParams["alternatives"] = term.alternatives;
					}

					actionParams["definition"] = convertToHtml(term.definition);

					return actionParams;
				}}
			/>
		</div>
	);
}

export interface IPageListQuery<T> {
	entities: null | T[];
}
interface IEntityTableProps<T extends IEntity> {
	pagingQuery: Class<IPageableListQuery<T>>;
	formClass: Class<T>;
	createExcludeFields?: Array<keyof T>;
	getInitialCreateData?: () => Partial<T>;
	updateExcludeFields?: Array<keyof T> | ((o: T) => Array<keyof T>);
	onAfterRefetch?: (o: T) => T;
	onBeforeSave?: (o: T) => boolean | object;
	editParams?: object;
	params?: object;
	paramsArticle?: object;
	canCreate?: boolean;
}
interface IEntityTableState<T> {
	editing: null | { type: "create" | "update"; data?: T };
	selectedValue?: Array<object>;
	updatedState: Boolean;
	loading?: Boolean;
	loaded?: Boolean;
}

@Entity({ update: "article_one_update" })
export class updateOnAdd {
	@Field()
	public id: any;

	@Field({ entity: GlossaryTerm })
	public glossaryTerms: GlossaryTerm[];
}

class GlossarySearch<T extends IEntity> extends React.Component<
	IEntityTableProps<T>,
	IEntityTableState<T>
> {
	public constructor(props: IEntityTableProps<T>) {
		super(props);
		this.state = {
			editing: null,
			updatedState: false,
			selectedValue: [],
			loading: false,
			loaded: true
		};
	}

	private onSelect = value => {
		this.setState({
			selectedValue: value,
			updatedState: true,
			loading: true,
			loaded: true
		});
	};

	private addArticle = (refresh: () => void) => {
		this.setState({
			loading: false,
			loaded: false
		});

		const param = { id: "", glossaryTermIds: [] } as any;
		if (this.state.selectedValue && this.state.selectedValue.length) {
			this.state.selectedValue.forEach(data => {
				param.glossaryTermIds.push(data["id"]);
			});
		}

		if (this.props.paramsArticle) {
			param.id = this.props.paramsArticle["articleId"];
			ServiceRegistry.getMutationService()
				.update(param, updateOnAdd)
				.then(res => {
					AppToaster.show({
						message: "Saved Successfully",
						intent: "success"
					});
					refresh();
					this.setState({
						loaded: true
					});
				})
				.catch(err => {});
		}
	};

	private onRemove = data => {
		this.setState({
			selectedValue: data,
			updatedState: true,
			loading: true
		});
	};

	public render() {
		const { pagingQuery, params, paramsArticle } = this.props;

		return (
			<Query.Root entity={pagingQuery} args={params}>
				{argsOne => {
					return (
						<Query.Root entity={pagingQuery} args={paramsArticle}>
							{argsTwo => {
								let SelectedValue = [];
								if (
									argsTwo &&
									argsTwo.data &&
									argsTwo.data.entities &&
									argsTwo.data.entities.length > 0
								) {
									SelectedValue = argsTwo.data.entities;
								}

								return (
									<div
										style={{
											marginBottom: "30px"
										}}
									>
										<div
											style={{
												display: "flex",
												gap: "10px",
												alignItems: "center"
											}}
										>
											<Multiselect
												options={
													argsOne &&
													argsOne.data &&
													argsOne.data.entities &&
													argsOne.data.entities
														.length > 0
														? argsOne.data.entities
														: []
												}
												selectedValues={
													this.state.updatedState
														? this.state
																.selectedValue
														: SelectedValue
												}
												onSelect={this.onSelect}
												displayValue="term"
												showCheckbox={true}
												style={{
													searchBox: {
														minWidth: "500px"
													}
												}}
												placeholder="Select Term"
												onRemove={this.onRemove}
											/>
											<div
												style={{
													display: "flex",
													gap: "10px",
													height: "max-content"
												}}
											>
												{this.state.loading &&
												this.props.canCreate ? (
													<Button
														text={
															<Label
																k="add"
																v="Add"
															/>
														}
														icon="small-plus"
														intent="primary"
														onClick={() =>
															this.addArticle(
																argsOne.forceRefresh
															)
														}
													/>
												) : !this.state.loaded ? (
													<Button
														text={
															<Label
																k="add"
																v="Add"
															/>
														}
														icon="small-plus"
														intent="primary"
														disabled={true}
														loading={true}
													/>
												) : (
													<Button
														text={
															<Label
																k="add"
																v="Add"
															/>
														}
														icon="small-plus"
														intent="primary"
														disabled={true}
													/>
												)}

												{this.props.canCreate ? (
													<Button
														text={
															<Label
																k="create"
																v="Create"
															/>
														}
														icon="small-plus"
														onClick={
															this.openCreate
														}
													/>
												) : (
													<Button
														text={
															<Label
																k="create"
																v="Create"
															/>
														}
														icon="small-plus"
														disabled={true}
													/>
												)}
											</div>
										</div>
										<Dialog
											isOpen={!!this.state.editing}
											onClose={this.close}
											title={
												this.state.editing &&
												this.state.editing.type ===
													"update" ? (
													<Label k="edit" v="Edit" />
												) : (
													<Label
														k="create"
														v="Create"
													/>
												)
											}
										>
											<div
												className={Classes.DIALOG_BODY}
											>
												{!!this.state.editing &&
													this.renderForm(
														argsOne.forceRefresh
													)}
											</div>
										</Dialog>
									</div>
								);
							}}
						</Query.Root>
					);
				}}
			</Query.Root>
		);
	}

	private renderForm = (refresh: () => void) => {
		if (!this.state.editing) {
			return null;
		}

		const {
			createExcludeFields,
			getInitialCreateData,
			onBeforeSave,
			onAfterRefetch,
			updateExcludeFields
		} = this.props;

		let formData: T;

		const isUpdate = this.state.editing.type === "update";

		if (this.state.editing.type === "update") {
			formData = this.state.editing.data || ({} as T);
		} else {
			formData = ((typeof getInitialCreateData === "function" &&
				getInitialCreateData()) ||
				{}) as T;
		}

		let excludeFields;

		if (isUpdate && updateExcludeFields) {
			excludeFields =
				typeof updateExcludeFields === "function"
					? updateExcludeFields(formData)
					: updateExcludeFields;
		} else {
			excludeFields = createExcludeFields;
		}

		return (
			<EntityForm
				clazz={this.props.formClass}
				instance={formData}
				refetch={isUpdate}
				supplementaryParams={this.props.editParams}
				excludedFields={excludeFields}
				afterRefetch={onAfterRefetch}
				beforeSave={onBeforeSave}
				afterSave={() => {
					this.setState(
						{
							editing: null,
							updatedState: false,
							selectedValue: [],
							loading: false
						},
						() => {
							AppToaster.show({
								message: "Saved Successfully",
								intent: "success"
							});
							refresh();
						}
					);
				}}
			/>
		);
	};
	private openCreate = () => this.setState({ editing: { type: "create" } });
	private close = () => this.setState({ editing: null });
}



// WEBPACK FOOTER //
// ./src/panels/kb/article/ArticleGlossaryTermListSearch.tsx